import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const PHASES = ['planning', 'permits', 'foundation', 'framing', 'systems', 'finishing', 'completion'];

const ProjectCard = ({ project, onSelect, isSelected }) => {
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-error/10 text-error border-error/20';
      case 'medium':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'low':
        return 'bg-success/10 text-success border-success/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getAduTypeIcon = (type) => {
    switch (type) {
      case 'detached': return 'Home';
      case 'attached': return 'Building2';
      case 'garage_conversion': return 'Warehouse';
      case 'basement_conversion': return 'Layers';
      default: return 'Home';
    }
  };

  const getProgressPercent = () => {
    const phaseIndex = PHASES?.findIndex(p => p === project?.phase);
    if (phaseIndex < 0) return 0;
    return Math.round(((phaseIndex + 1) / PHASES?.length) * 100);
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const progressPercent = getProgressPercent();
  const dueDate = formatDate(project?.dueDate);

  return (
    <div
      onClick={() => onSelect(project)}
      className={`bg-card rounded-xl p-4 sm:p-5 border-2 transition-all duration-250 cursor-pointer hover:shadow-lg min-h-[44px] ${
        isSelected ? 'border-accent shadow-md' : 'border-border hover:border-accent/50'
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Icon name={getAduTypeIcon(project?.aduType)} size={20} color="var(--color-primary)" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm text-foreground line-clamp-1">
              {project?.customerName}
            </h3>
            <p className="text-xs text-muted-foreground line-clamp-1">
              {project?.address}
            </p>
          </div>
        </div>
        <span className={`px-2 py-1 rounded-md text-xs font-medium border whitespace-nowrap ${getPriorityColor(project?.priority)}`}>
          {project?.priority?.charAt(0)?.toUpperCase() + project?.priority?.slice(1)}
        </span>
      </div>

      <div className="space-y-1.5 mb-3">
        <div className="flex items-center gap-1.5 text-xs">
          <Icon name="Building2" size={13} className="text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground capitalize">{project?.aduType?.replace('_', ' ')}</span>
        </div>
        {project?.nextMilestone && (
          <div className="flex items-center gap-1.5 text-xs">
            <Icon name="Target" size={13} className="text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground line-clamp-1">Next: {project?.nextMilestone}</span>
          </div>
        )}
        {dueDate && (
          <div className="flex items-center gap-1.5 text-xs">
            <Icon name="Calendar" size={13} className="text-muted-foreground flex-shrink-0" />
            <span className="text-muted-foreground">Due: {dueDate}</span>
          </div>
        )}
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-muted-foreground">Progress</span>
          <span className="text-xs font-semibold text-primary">{progressPercent}%</span>
        </div>
        <div className="w-full bg-border rounded-full h-1.5">
          <div
            className="bg-primary rounded-full h-1.5 transition-all duration-300"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-border">
        <div className="flex -space-x-1.5 flex-shrink-0">
          {project?.contractors?.slice(0, 3)?.map((contractor, index) => (
            <div
              key={index}
              className="w-7 h-7 rounded-full border-2 border-card overflow-hidden"
              title={contractor?.name}
            >
              <Image
                src={contractor?.avatar}
                alt={contractor?.avatarAlt || contractor?.name || 'Contractor'}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          {project?.contractors?.length > 3 && (
            <div className="w-7 h-7 rounded-full border-2 border-card bg-muted flex items-center justify-center">
              <span className="text-xs font-medium text-muted-foreground">
                +{project?.contractors?.length - 3}
              </span>
            </div>
          )}
        </div>
        <span className="text-xs text-muted-foreground flex-1 min-w-0 line-clamp-1">
          {project?.contractors?.length || 0} contractor{(project?.contractors?.length || 0) !== 1 ? 's' : ''}
        </span>
      </div>
    </div>
  );
};

export default ProjectCard;